/**
 * 사업장·위치 — 이 서버의 화면을 **지역 › 사업장 › 위치** 로 정리한다(2026-10-21 사용자 지시 "사업장, 공장 등 위치
 * 관련 정보를 관리하는 화면").
 *
 * 한 표에 위치마다 지역·사업장·화면 수를 두고 고친다. 위치 이름 바꾸기·지우기도 같은 줄에서 한다. 오른쪽은 고치는 중인
 * 값으로 그린 구성(조직 › 지역 › 사업장 › 위치) — 저장 전에 어떻게 묶이는지 본다. 규칙은 `locations.ts`.
 *
 * **층 이름**(2026-10-26 사용자 결정 "공장 등 언어를 더 범용적으로")은 여기서 고른다 — 범용(지역 › 사업장 › 위치)·제조
 * (지역 › 공장 › 건물)·유통(권역 › 매장 › 구역) 같은 묶음이나 직접 입력(`siteLabels.ts`). 화면의 글자만 바뀌고 저장 칸은
 * 그대로다. 저장하면 레이아웃의 값(`siteLabelsContext`)도 갈아 끼워 메뉴 부제·모니터링이 곧바로 같은 말을 쓴다.
 * 아래 주석의 "지역·공장" 은 그 전에 붙인 이름 — 첫째·둘째 층이다.
 *
 * 맨 위의 **조직 이름**은 세 층 위의 한 이름이다(2026-10-22) — 모니터링 왼쪽 나무의 머리가 된다(비면 "전체").
 * **주소**(2026-10-22)는 위치마다 단다 — 주소 DB 에서 검색해 고르고(`AddressDialog`), 그 좌표로 모니터링 지도에 놓는다.
 * 주소를 적지 않은 위치는 이 서버의 기본 주소를 따른다.
 *
 * 누구나 본다(읽기 권한). 지역·공장을 정하는 것은 화면 관리 권한(`fleet:write`), 위치 이름을 바꾸고 지우는 것은 편성
 * 권한(`schedule:write`)이 있고 **담당 범위가 없는** 계정만 된다(서버도 403) — 사업장 구성과 위치 이름은 남의 그룹
 * 화면도 함께 쓴다.
 *
 * 통합 관제로 묶은 다른 서버의 지역·공장은 여기서 다루지 않는다 — 모니터링의 [사업장 구성] 에서 정한다(그 서버의 위치
 * 이름은 그 서버에서 고친다). 저장할 때 다른 서버의 줄은 그대로 둔다(`mergeSiteEntries`).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Pencil, Trash2 } from 'lucide-react';
import type { Me, SignageOneClient, SiteAddress } from '../api/client.js';
import { can } from '../capabilities.js';
import { useAction, useAsync } from '../hooks.js';
import {
  applyEdits,
  changedPlaceCount,
  inheritedPlace,
  localSiteSource,
  locationRows,
  NO_EDITS,
  placeNames,
  placeStats,
  renameProblem,
  renameSummary,
  type PlaceEdits,
  type PlacePatch,
} from '../locations.js';
import {
  addressLine,
  buildSiteTree,
  draftFromEntries,
  LOCAL_SOURCE,
  mergeSiteEntries,
  pointOf,
  siteTitle,
  entriesFromDraft,
} from '../sites.js';
import {
  CUSTOM_PRESET,
  fillSiteLabels,
  presetOf,
  resolveSiteLabels,
  sameSiteLabels,
  SITE_LABEL_MAX,
  SITE_LABEL_PRESETS,
  siteLabelProblem,
  storedSiteLabels,
  unassignedLocation,
  unnamedRegion,
  withParticle,
  type SiteLabels,
} from '../siteLabels.js';
import { useUpdateSiteLabels } from '../components/siteLabelsContext.js';
import { AddressDialog } from '../components/AddressDialog.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';
import { Empty, ErrorNote, ItemTitle, Loading, Panel } from '../components/ui.js';

export function LocationsScreen({ client, me }: { client: SignageOneClient; me: Me }): ReactNode {
  const navigate = useNavigate();
  /** 지역·공장을 정할 수 있는가 — 사업장 구성 저장(`PUT /sites`)과 같은 조건. */
  const canPlace = can(me, 'fleet:write') && !me.scope;
  /** 위치 이름을 바꾸고 지울 수 있는가 — `POST /displays/locations/*` 와 같은 조건. */
  const canName = can(me, 'schedule:write') && !me.scope;

  const sites = useAsync(() => client.sites(), [client]);
  const listed = useAsync(() => client.listLocations(), [client]);
  const summary = useAsync(() => client.fleetSummary({ locations: true }), [client]);
  const action = useAction();
  const [edits, setEdits] = useState<PlaceEdits>(NO_EDITS);
  /** 고치는 중인 조직 이름 — null 이면 손대지 않았다. */
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  /** 고치는 중인 층 이름 — null 이면 손대지 않았다. 직접 입력을 고르면 칸 셋이 열린다. */
  const [labelsDraft, setLabelsDraft] = useState<SiteLabels | null>(null);
  const [customLabels, setCustomLabels] = useState(false);
  const updateSiteLabels = useUpdateSiteLabels();
  const [renaming, setRenaming] = useState<{ from: string; draft: string } | null>(null);
  /** 주소 창 — 위치 이름(빈 문자열은 위치 미지정), null 이면 이 서버의 기본 주소. */
  const [addressing, setAddressing] = useState<{ location: string | null } | null>(null);

  const rows = useMemo(
    () => locationRows(listed.data?.items ?? [], summary.data?.locations ?? []),
    [listed.data, summary.data],
  );
  const source = useMemo(() => localSiteSource(rows, summary.data?.as_of ?? null), [rows, summary.data]);
  const entries = sites.data?.entries ?? [];
  const saved = useMemo(() => draftFromEntries([source], entries)[LOCAL_SOURCE]!, [source, entries]);
  const draft = applyEdits(saved, edits);
  const savedName = sites.data?.name ?? '';
  const siteName = nameDraft ?? savedName;
  const nameChanged = nameDraft !== null && nameDraft.trim() !== savedName.trim();
  const savedLabels = useMemo(() => resolveSiteLabels(sites.data?.labels), [sites.data]);
  /** 이 화면의 글자에 쓰는 층 이름 — 고치는 중이면 그 값으로 미리 본다. */
  const labels = resolveSiteLabels(labelsDraft ?? savedLabels);
  const labelsChanged = labelsDraft !== null && !sameSiteLabels(labelsDraft, savedLabels);
  const labelsProblem = labelsDraft
    ? (siteLabelProblem(labelsDraft.region) ?? siteLabelProblem(labelsDraft.site) ?? siteLabelProblem(labelsDraft.location))
    : null;
  const preset = customLabels ? CUSTOM_PRESET : presetOf(labels);
  /** 저장할 칸 수 — 조직 이름·층 이름과 지역·사업장·주소. */
  const changed = changedPlaceCount(saved, draft) + (nameChanged ? 1 : 0) + (labelsChanged ? 1 : 0);
  const tree = buildSiteTree([source], entriesFromDraft({ [LOCAL_SOURCE]: draft }), labels);
  const stats = placeStats(tree);
  const names = placeNames(draft, entries);
  const inherited = inheritedPlace(draft, labels);
  const unassigned = unassignedLocation(labels);
  /** 표 머리·칸 이름의 셋째 층 — 기본("위치")이 아니면 화면의 위치라는 것을 함께 적는다. */
  const locationHead = labels.location === '위치' ? '위치' : `${labels.location}(위치)`;

  // 읽어 온 층 이름을 레이아웃에도 — 다른 사람이 바꾼 것을 메뉴 부제가 늦게 따라오지 않게.
  useEffect(() => {
    if (sites.data) updateSiteLabels(sites.data.labels);
  }, [sites.data, updateSiteLabels]);
  const remoteEntries = entries.some((entry) => entry.source !== LOCAL_SOURCE);
  const loading =
    (sites.loading && !sites.data) || (listed.loading && !listed.data) || (summary.loading && !summary.data);

  const reloadAll = (): void => {
    setRenaming(null);
    sites.reload();
    listed.reload();
    summary.reload();
  };

  const setBase = (patch: PlacePatch): void =>
    setEdits((current) => ({ ...current, base: { ...current.base, ...patch } }));
  const setPlace = (location: string, patch: PlacePatch): void =>
    setEdits((current) => ({
      ...current,
      locations: { ...current.locations, [location]: { ...current.locations[location], ...patch } },
    }));

  const revert = (): void => {
    setEdits(NO_EDITS);
    setNameDraft(null);
    setLabelsDraft(null);
    setCustomLabels(false);
  };

  const save = (): void => {
    if (!sites.data || changed === 0 || labelsProblem) return;
    void action.run(
      () =>
        client.saveSites({
          name: siteName.trim(),
          labels: storedSiteLabels(labels),
          entries: mergeSiteEntries(entries, { [LOCAL_SOURCE]: draft }),
          revision: sites.data!.revision,
        }),
      (view) => {
        revert();
        updateSiteLabels(view.labels);
        sites.reload();
      },
      '사업장 구성을 저장했습니다',
    );
  };

  const choosePreset = (id: string): void => {
    if (id === CUSTOM_PRESET) {
      setCustomLabels(true);
      setLabelsDraft(labels);
      return;
    }
    const chosen = SITE_LABEL_PRESETS.find((item) => item.id === id);
    if (!chosen) return;
    setCustomLabels(false);
    setLabelsDraft(chosen.labels);
  };
  const setLabel = (key: keyof SiteLabels, value: string): void =>
    setLabelsDraft((current) => ({ ...(current ?? labels), [key]: value }));

  const rename = async (from: string, draftName: string): Promise<void> => {
    if (renameProblem(from, draftName)) return;
    const to = draftName.trim();
    const target = rows.find((row) => row.name === to);
    if (target) {
      const ok = await confirmDialog({
        title: `${labels.location} 합치기`,
        body: (
          <>
            <strong>{to}</strong> {withParticle(labels.location, '이')} 이미 있습니다(화면 {target.displays}대).{' '}
            <strong>{from}</strong> 의 화면을 그 {withParticle(labels.location, '으로')} 합칩니다. {labels.region}·
            {withParticle(labels.site, '과')} 모니터링 배치는 <strong>{to}</strong> 의 것을 남깁니다.
          </>
        ),
        confirmLabel: '합치기',
      });
      if (!ok) return;
    }
    void action.run(
      () => client.renameLocation(from, to),
      reloadAll,
      (result) => renameSummary(from, to, result, labels.location),
    );
  };

  const remove = async (name: string, count: number): Promise<void> => {
    const ok = await confirmDialog({
      title: `${labels.location} 지우기`,
      body: (
        <>
          <strong>{name}</strong> 의 화면 {count}대가 <strong>{unassigned}</strong>으로 돌아갑니다. 화면은 지워지지
          않습니다. 이 {labels.location}의 {labels.region}·{labels.site} 지정과 모니터링 배치도 함께 지워집니다.
        </>
      ),
      confirmLabel: '지우기',
      danger: true,
    });
    if (!ok) return;
    void action.run(
      () => client.deleteLocation(name),
      reloadAll,
      (result) => `'${name}' ${withParticle(labels.location, '을')} 지웠습니다 — 화면 ${result.displays}대`,
    );
  };

  /** 이름 바꾸기·지우기는 저장하지 않은 변경이 없을 때만 — 서버가 사업장 구성을 함께 옮겨 판이 바뀐다. */
  const nameBlocked =
    changed > 0 ? `조직 이름·층 이름·${labels.region}·${labels.site}·주소 변경을 먼저 저장하거나 되돌리세요` : null;

  /** 주소 칸 — 적었으면 그 주소(좌표가 없으면 표시), 아니면 따를 기본 주소를 흐리게. 고칠 수 있으면 누르면 창이 열린다. */
  const addressCell = (own: SiteAddress | null, location: string | null, label: string): ReactNode => {
    const line = addressLine(own);
    const shown = line || (location === null ? '' : inherited.address);
    const text = shown || '주소 없음';
    const body = (
      <>
        <MapPin className={`h-3.5 w-3.5 shrink-0 ${line ? (pointOf(own) ? 'text-emerald-600' : 'text-amber-600') : 'text-slate-300'}`} aria-hidden="true" />
        <span className={`min-w-0 truncate ${line ? 'text-slate-700' : 'text-slate-400'}`}>{text}</span>
        {line && !pointOf(own) ? <span className="shrink-0 text-[11px] text-amber-700">좌표 없음</span> : null}
      </>
    );
    return canPlace ? (
      <button
        type="button"
        className="location-address"
        title={line ? `${line} — 눌러서 바꾸기` : '눌러서 주소 정하기'}
        aria-label={`${label} 주소`}
        onClick={() => setAddressing({ location })}
      >
        {body}
      </button>
    ) : (
      <span className="location-address is-static" title={shown || undefined}>
        {body}
      </span>
    );
  };

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {sites.error || listed.error || summary.error ? (
        <ErrorNote message={(sites.error ?? listed.error ?? summary.error)!} />
      ) : null}

      {/* 두 카드가 나란히 서는 넓은 화면에서만 본문 높이까지 늘린다 — 위아래로 쌓이면 줄마다 반씩 늘어나 보인다. */}
      <div className="screen-fill grid content-start gap-4 xl:grid-cols-3 xl:content-stretch">
        <Panel
          className="xl:col-span-2"
          title="사업장·위치"
          subtitle={`${labels.region} ${stats.regions} · ${labels.site} ${stats.sites} · ${labels.location} ${stats.locations} · 화면 ${stats.displays}대`}
          help={fillSiteLabels(
            '화면을 조직 아래 {region} › {site} › {location} 세 층으로 묶습니다. 조직 이름은 모니터링 나무의 맨 위에 나옵니다. {location:은} 화면마다 적은 위치이고, {region}·{site:은} 여기서 위치마다 정합니다. 층을 부르는 말은 [층 이름] 에서 업종에 맞게 고릅니다(예: 제조 — 지역 › 공장 › 건물). 비워 둔 칸은 이 서버의 기본값을 따릅니다. 모니터링의 사업장 현황과 대시보드의 위치별 현황이 이 구성을 씁니다.',
            labels,
          )}
          actions={
            canPlace && changed > 0 ? (
              <>
                <span className="hint whitespace-nowrap">저장하지 않은 변경 {changed}곳</span>
                <button type="button" className="btn" disabled={action.pending} onClick={revert}>
                  되돌리기
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={action.pending || !sites.data || labelsProblem !== null}
                  title={labelsProblem ? `층 이름: ${labelsProblem}` : undefined}
                  onClick={save}
                >
                  {action.pending ? '저장 중…' : '저장'}
                </button>
              </>
            ) : null
          }
        >
          {loading ? <Loading /> : null}

          <datalist id="location-regions">
            {names.regions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <datalist id="location-sites">
            {names.sites.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {/*
            층 이름 — 세 층을 무엇이라 부를지(업종별 묶음 또는 직접 입력). 고르는 즉시 이 화면의 글자와 오른쪽 구성이 그 말로
            바뀌어 저장 전에 본다. 화면 관리 권한이 없으면 지금 이름만 보인다.
          */}
          <div className="grid gap-3 border-b border-slate-100 px-4 py-3 sm:grid-cols-3">
            <label className="field">
              <span>층 이름</span>
              {canPlace ? (
                <select className="input" value={preset} onChange={(event) => choosePreset(event.target.value)}>
                  {SITE_LABEL_PRESETS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {/* 층 이름이 앞 — 칸이 좁아 잘려도 고른 말은 보이게. */}
                      {item.labels.region} › {item.labels.site} › {item.labels.location} ({item.name})
                    </option>
                  ))}
                  <option value={CUSTOM_PRESET}>직접 입력</option>
                </select>
              ) : (
                <span className="cell-strong">
                  {labels.region} › {labels.site} › {labels.location}
                </span>
              )}
            </label>
            {canPlace && preset === CUSTOM_PRESET ? (
              <div className="field sm:col-span-2">
                <span>직접 입력 — 칸마다 {SITE_LABEL_MAX}자까지, 비우면 기본(지역 · 사업장 · 위치)</span>
                <div className="flex items-center gap-1.5">
                  {(['region', 'site', 'location'] as const).map((key, index) => (
                    <span key={key} className="flex min-w-0 flex-1 items-center gap-1.5">
                      {index > 0 ? <span className="text-slate-300" aria-hidden="true">›</span> : null}
                      <input
                        className="input min-w-0 flex-1"
                        aria-label={['첫째 층 이름', '둘째 층 이름', '셋째 층 이름'][index]}
                        placeholder={['지역', '사업장', '위치'][index]}
                        maxLength={SITE_LABEL_MAX}
                        value={(labelsDraft ?? labels)[key]}
                        onChange={(event) => setLabel(key, event.target.value)}
                      />
                    </span>
                  ))}
                </div>
                {labelsProblem ? <span className="text-[12px] text-red-600">{labelsProblem}</span> : null}
              </div>
            ) : (
              <p className="hint self-end sm:col-span-2">
                {withParticle(labels.region, '은')} 여러 {withParticle(labels.site, '을')} 묶는 단위, {withParticle(labels.site, '은')} 주소가 있는 한
                곳, {withParticle(labels.location, '은')} 그 안에서 화면마다 적은 위치입니다. 화면의 글자만 바뀌고 저장된 값은
                그대로입니다.
              </p>
            )}
          </div>

          {/*
            조직 이름(모니터링 나무의 머리)과 이 서버의 기본값 — 위치마다 정하지 않은 칸이 기본값을 따른다. 사업장 하나짜리
            서버는 이 줄만 정하면 된다.
          */}
          <div className="grid gap-3 border-b border-slate-100 px-4 py-3 sm:grid-cols-3">
            <label className="field">
              <span>조직 이름</span>
              {canPlace ? (
                <input
                  className="input"
                  placeholder="전체"
                  maxLength={60}
                  value={siteName}
                  onChange={(event) => setNameDraft(event.target.value)}
                />
              ) : (
                <span className="cell-strong">{siteTitle(savedName)}</span>
              )}
            </label>
            <label className="field">
              <span>기본 {labels.region}</span>
              {canPlace ? (
                <input
                  className="input"
                  list="location-regions"
                  placeholder={unnamedRegion(labels)}
                  maxLength={60}
                  value={draft.region}
                  onChange={(event) => setBase({ region: event.target.value })}
                />
              ) : (
                <span className="cell-strong">{inherited.region}</span>
              )}
            </label>
            <label className="field">
              <span>기본 {labels.site}</span>
              {canPlace ? (
                <input
                  className="input"
                  list="location-sites"
                  placeholder="본 서버"
                  maxLength={60}
                  value={draft.site}
                  onChange={(event) => setBase({ site: event.target.value })}
                />
              ) : (
                <span className="cell-strong">{inherited.site}</span>
              )}
            </label>
            <div className="field sm:col-span-3">
              <span>기본 주소</span>
              {addressCell(draft.address, null, '기본')}
            </div>
          </div>

          {!loading && rows.length === 0 ? (
            <Empty
              title="아직 위치를 적은 화면이 없습니다"
              hint="화면 추가·화면 상세·화면 목록의 일괄 변경에서 위치를 적으면 여기에 나옵니다."
            />
          ) : null}

          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    {/* 머리 글자가 짧으면("위치") 칸이 한 글자 폭까지 줄어 이름이 세로로 쪼개진다 — 폭을 받쳐 둔다. */}
                    <th className="min-w-[8rem]">{locationHead}</th>
                    <th>{labels.region}</th>
                    <th>{labels.site}</th>
                    <th>주소</th>
                    <th>화면</th>
                    {canName ? <th aria-label="동작" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const place = draft.buildings[row.name] ?? { region: '', site: '', address: null };
                    const label = row.name || unassigned;
                    const isRenaming = renaming?.from === row.name;
                    const problem = isRenaming ? renameProblem(row.name, renaming!.draft) : null;
                    return (
                      <tr key={row.name || '\u0000'}>
                        <td className={row.name ? 'cell-strong' : 'cell-muted whitespace-nowrap'}>
                          {isRenaming ? (
                            <input
                              className="input input-sm w-full"
                              value={renaming!.draft}
                              maxLength={200}
                              aria-label={`${row.name} 새 이름`}
                              autoFocus
                              disabled={action.pending}
                              onChange={(event) => setRenaming({ from: row.name, draft: event.target.value })}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') void rename(row.name, renaming!.draft);
                                if (event.key === 'Escape') setRenaming(null);
                              }}
                            />
                          ) : (
                            // 이름을 누르면 [이름 바꾸기] 와 같다. 위치 미지정은 이름이 아니라 바꿀 것이 없다.
                            <ItemTitle
                              className=""
                              onOpen={
                                canName && row.name && nameBlocked === null && !action.pending
                                  ? () => setRenaming({ from: row.name, draft: row.name })
                                  : undefined
                              }
                              label={`${row.name} 이름 바꾸기`}
                            >
                              {label}
                            </ItemTitle>
                          )}
                        </td>
                        <td>
                          {canPlace ? (
                            <input
                              className="input input-sm w-full min-w-[6.5rem]"
                              list="location-regions"
                              aria-label={`${label} ${labels.region}`}
                              placeholder={inherited.region}
                              maxLength={60}
                              value={place.region}
                              onChange={(event) => setPlace(row.name, { region: event.target.value })}
                            />
                          ) : (
                            <span className={place.region ? undefined : 'cell-muted'}>
                              {place.region || inherited.region}
                            </span>
                          )}
                        </td>
                        <td>
                          {canPlace ? (
                            <input
                              className="input input-sm w-full min-w-[6.5rem]"
                              list="location-sites"
                              aria-label={`${label} ${labels.site}`}
                              placeholder={inherited.site}
                              maxLength={60}
                              value={place.site}
                              onChange={(event) => setPlace(row.name, { site: event.target.value })}
                            />
                          ) : (
                            <span className={place.site ? undefined : 'cell-muted'}>
                              {place.site || inherited.site}
                            </span>
                          )}
                        </td>
                        <td className="w-[13rem] min-w-[11rem] max-w-[13rem]">{addressCell(place.address, row.name, label)}</td>
                        <td className="whitespace-nowrap">
                          {/* 위치가 있으면 그 위치로 거른 화면 목록으로 — 어느 화면들인지 거기서 본다. */}
                          {row.name ? (
                            <button
                              type="button"
                              className="link"
                              title={`'${row.name}' 화면 목록 보기`}
                              onClick={() => navigate(`/displays?loc=${encodeURIComponent(row.name)}`)}
                            >
                              {row.displays}대
                            </button>
                          ) : (
                            `${row.displays}대`
                          )}
                          <span className="hint"> · 켜짐 {row.online}</span>
                        </td>
                        {canName ? (
                          <td>
                            {/* 위치 미지정은 이름이 아니라 바꾸거나 지울 것이 없다. */}
                            {row.name ? (
                              <div className="flex justify-end gap-1.5">
                                {isRenaming ? (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-tiny btn-primary"
                                      disabled={action.pending || problem !== null}
                                      title={problem ?? undefined}
                                      onClick={() => void rename(row.name, renaming!.draft)}
                                    >
                                      저장
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-tiny"
                                      disabled={action.pending}
                                      onClick={() => setRenaming(null)}
                                    >
                                      취소
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <IconButton
                                      icon={Pencil}
                                      label={nameBlocked ?? '이름 바꾸기'}
                                      subject={row.name}
                                      disabled={action.pending || nameBlocked !== null}
                                      onClick={() => setRenaming({ from: row.name, draft: row.name })}
                                    />
                                    <IconButton
                                      icon={Trash2}
                                      label={nameBlocked ?? '지우기'}
                                      subject={row.name}
                                      danger
                                      disabled={action.pending || nameBlocked !== null}
                                      onClick={() => void remove(row.name, row.displays)}
                                    />
                                  </>
                                )}
                              </div>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          <p className="hint px-4 py-3">
            {withParticle(labels.location, '은')} 화면마다 적은 위치라 여기서 새로 만들지 않습니다 — 화면 추가·화면 상세·화면
            목록의 일괄 변경에서 적습니다. 이름을 바꾸면 그 {labels.location}의 화면 전부(숨긴 화면 포함)가 함께 바뀌고, 이미
            있는 이름으로 바꾸면 두 {withParticle(labels.location, '이')} 합쳐집니다.
            {remoteEntries
              ? ` 통합 관제로 묶은 다른 서버의 ${labels.region}·${withParticle(labels.site, '은')} 모니터링의 [사업장 구성] 에서 정합니다.`
              : ''}
          </p>
        </Panel>

        {/* 고치는 중인 값으로 그린 구성 — 저장 전에 어떻게 묶이는지 본다. 모니터링의 사업장 나무와 같은 규칙(`buildSiteTree`). */}
        <Panel title="구성" subtitle={changed > 0 ? '저장하지 않은 변경을 반영한 모습' : undefined}>
          {/* 모니터링 나무의 머리와 같은 이름 — 조직 하나 아래 지역들. */}
          <p className="border-b border-slate-100 px-4 py-2.5 text-[14px] font-semibold text-slate-900">
            {siteTitle(siteName)}
          </p>
          {tree.length === 0 ? (
            <p className="hint p-4">화면이 없습니다.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {tree.map((region) => (
                <li key={region.key} className="px-4 py-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13.5px] font-semibold text-slate-800">{region.name}</span>
                    <span className="hint whitespace-nowrap">
                      {labels.site} {region.factories.length} · 화면 {region.total}대
                    </span>
                  </div>
                  <ul className="mt-1.5 space-y-1.5">
                    {region.factories.map((factory) => (
                      <li key={factory.key} className="rounded-md bg-slate-50 px-3 py-2">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-[13px] font-medium text-slate-700">{factory.name}</span>
                          <span className="hint whitespace-nowrap">
                            화면 {factory.total}대 · 켜짐 {factory.online}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[12px] text-slate-500">
                          {factory.buildings.map((building) => `${building.name}(${building.total})`).join(' · ')}
                        </p>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {addressing ? (
        <AddressDialog
          client={client}
          title={addressing.location === null ? '기본 주소' : `${addressing.location || unassigned} 주소`}
          value={addressing.location === null ? draft.address : (draft.buildings[addressing.location]?.address ?? null)}
          inherited={addressing.location === null ? undefined : inherited.address || undefined}
          onApply={(next) =>
            addressing.location === null ? setBase({ address: next }) : setPlace(addressing.location, { address: next })
          }
          onClose={() => setAddressing(null)}
        />
      ) : null}
    </div>
  );
}
